import dayjs from "dayjs";
import { monthKeyOf } from "./utils";
import {
    ST_SET_LOADING,
    ST_SET_MONTH_GRID,
    ST_SET_STATUSES,
    ST_SET_FLAG_METADATA,
    ST_SET_FILTER,
    ST_PATCH_CELL_STATUS,
    ST_SET_V2_ENABLED,
    ST_SET_TAGGING_MODE,
    ST_ADD_COACH_FLAG,
    ST_ADD_COACH_FLAGS,
    ST_REMOVE_COACH_FLAG,
    ST_REMOVE_COACH_FLAGS,
} from "./constants";

// One cell with `flagId` dropped from its user_defined flags. Returns `monthCells` unchanged
// when there is nothing to remove, so a no-op never re-renders. The backend deletes the whole
// row once its last coach entry goes, so an emptied user_defined list is dropped here too —
// that is what returns the cell to "no flags" for the badge.
const withoutCoachFlag = (monthCells, uniqueKey, flagId) => {
    const existing = monthCells[uniqueKey];
    if (!existing) return monthCells;
    const flags = existing.flags && !Array.isArray(existing.flags) ? existing.flags : {};
    const userDefined = Array.isArray(flags.user_defined) ? flags.user_defined : [];
    const remaining = userDefined.filter((f) => f.user_defined_flag_id !== flagId);
    if (remaining.length === userDefined.length) return monthCells;
    const nextFlags = { ...flags };
    if (remaining.length) nextFlags.user_defined = remaining;
    else delete nextFlags.user_defined;
    return { ...monthCells, [uniqueKey]: { ...existing, flags: nextFlags } };
};

// One cell with `flag` appended to its user_defined flags. A cell with no flags carries [] or
// {}, so normalise first. Re-tagging the same flag rewrites its note, mirroring the backend upsert.
const withCoachFlag = (monthCells, uniqueKey, flag) => {
    const existing = monthCells[uniqueKey] || {};
    const flags = existing.flags && !Array.isArray(existing.flags) ? existing.flags : {};
    const userDefined = Array.isArray(flags.user_defined) ? flags.user_defined : [];
    const others = userDefined.filter((f) => f.user_defined_flag_id !== flag.user_defined_flag_id);
    return {
        ...monthCells,
        [uniqueKey]: { ...existing, flags: { ...flags, user_defined: [...others, flag] } },
    };
};

// "YYYY-MM-DD" → its byMonth key, the same "YYYY-MM" that monthKeyOf gives.
const monthOfDate = (date) => date.slice(0, 7);

export const initialState = {
    byMonth: {}, // { 'YYYY-MM': { '<pid>_<date>': cell } } — month-scoped cache
    statuses: [],
    flagMetadata: {}, // { 'YYYY-MM-DD': [ {player_id, ...} ] } — GPS checklist meta, fetched lazily
    flagMetaMonths: {}, // { 'YYYY-MM': true } — which months' flag meta have been fetched
    filters: {
        playersFilter: [],
        positionsFilter: [],
        playerGroupsFilter: [],
        playerStatusesFilter: [],
        flagTypesFilter: [], // "gps" | "forceplate" | "testing" | "udf:<user_defined_flag_id>"
        selectedMonth: dayjs(),
    },
    loading: true, // start loading so the first paint is the skeleton, not an empty table
    // The V2 gate is env-driven on the backend with nothing exposed on the team, so the only
    // signal is the month-view 403. null until the first load answers.
    v2Enabled: null,
    // null when off, else "single" | "bulk" — the grid tags cells instead of opening the modal.
    taggingMode: null,
};

export const reducer = (state, action) => {
    switch (action.type) {
        case ST_SET_LOADING:
            return { ...state, loading: action.payload };
        case ST_SET_V2_ENABLED:
            return { ...state, v2Enabled: action.payload };
        case ST_SET_TAGGING_MODE:
            return { ...state, taggingMode: action.payload };
        case ST_ADD_COACH_FLAG: {
            // Local cache patch after a tag — same policy as ST_PATCH_CELL_STATUS, no grid refetch.
            const mKey = monthKeyOf(state.filters.selectedMonth);
            const { uniqueKey, flag } = action.payload;
            return {
                ...state,
                byMonth: { ...state.byMonth, [mKey]: withCoachFlag(state.byMonth[mKey] || {}, uniqueKey, flag) },
            };
        }
        case ST_REMOVE_COACH_FLAG: {
            const mKey = monthKeyOf(state.filters.selectedMonth);
            const monthCells = state.byMonth[mKey] || {};
            const { uniqueKey, flagId } = action.payload;
            const next = withoutCoachFlag(monthCells, uniqueKey, flagId);
            if (next === monthCells) return state;
            return { ...state, byMonth: { ...state.byMonth, [mKey]: next } };
        }
        // The bulk dialog can save any date, not only the tracker's month. So each cell goes to
        // its own month's bucket, and only to a bucket already loaded: a bucket made here would
        // hold coach tags alone, and the provider would then never fetch that month.
        case ST_REMOVE_COACH_FLAGS: {
            const { flagId, uniqueKeys } = action.payload;
            const byMonth = { ...state.byMonth };
            uniqueKeys.forEach((key) => {
                const mKey = monthOfDate(key.split("_")[1]);
                if (byMonth[mKey]) byMonth[mKey] = withoutCoachFlag(byMonth[mKey], key, flagId);
            });
            return { ...state, byMonth };
        }
        case ST_ADD_COACH_FLAGS: {
            const byMonth = { ...state.byMonth };
            action.payload.forEach((tag) => {
                const mKey = monthOfDate(tag.date);
                if (byMonth[mKey]) byMonth[mKey] = withCoachFlag(byMonth[mKey], `${tag.player_id}_${tag.date}`, tag);
            });
            return { ...state, byMonth };
        }
        case ST_SET_MONTH_GRID:
            return {
                ...state,
                byMonth: { ...state.byMonth, [action.payload.monthKey]: action.payload.cells },
            };
        case ST_SET_STATUSES:
            return { ...state, statuses: action.payload };
        case ST_SET_FLAG_METADATA:
            return {
                ...state,
                flagMetadata: { ...state.flagMetadata, ...action.payload.meta },
                flagMetaMonths: { ...state.flagMetaMonths, [action.payload.monthKey]: true },
            };
        case ST_SET_FILTER:
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case ST_PATCH_CELL_STATUS: {
            // Local cache patch on status assign/clear — no grid refetch (mutation policy).
            const mKey = monthKeyOf(state.filters.selectedMonth);
            const monthCells = state.byMonth[mKey] || {};
            const { uniqueKey, status } = action.payload;
            const existing = monthCells[uniqueKey] || {};
            return {
                ...state,
                byMonth: {
                    ...state.byMonth,
                    [mKey]: { ...monthCells, [uniqueKey]: { ...existing, status } },
                },
            };
        }
        default:
            return state;
    }
};
